import { useMemo } from 'react';

import { DateFormat } from '@heidi/schema/ml-scribe';
import { convertMomentToDateFnsFormat } from '@heidi/utils/dates';
import { useQueryUser } from '@heidi/workspace/data';

/**
 * The format a user with no `session_settings.date_format` sees. The app names
 * the same enum member `DEFAULT_DATE_FORMAT` in `@/components/constants`, which
 * is an app barrel this zone may not import; the value it aliases is a schema
 * enum member, so this reads the schema door instead.
 */
const FALLBACK_DATE_FORMAT = DateFormat.DAY_MONTH_YEAR;

/**
 * The signed-in user's saved date format, as a date-fns format string.
 *
 * This is the read a design-system date field may NOT do for itself:
 * `@heidi/design-system` is `type:ui`, the format's door is `type:domain`, and
 * the type axis refuses that edge — so the format reaches `DatePicker` as a
 * required prop from a surface that already knows the user.
 *
 * It lives here rather than app-side because `domainAllowList('patients')` is
 * `['patients', 'shared', 'workspace']`: foundational workspace needs no
 * per-domain edge, so a patients hook may read this door directly. Modelled on
 * `useUserDateFormatter` in `@heidi/coding`, which returns a formatting
 * FUNCTION for read-only display; a field that also PARSES what a user types
 * needs the format string itself.
 *
 * Two patients surfaces share it — the create-patient drawer and the
 * extraction-review section — which is why it is one hook here rather than a
 * read at each mount point.
 */
export function useUserDateFormat(): string {
  const { data: savedDateFormat } = useQueryUser(
    (data) => data.session_settings?.date_format
  );

  return useMemo(
    () => convertMomentToDateFnsFormat(savedDateFormat ?? FALLBACK_DATE_FORMAT),
    [savedDateFormat]
  );
}
